// RiskGradeBadge.tsx - A-F Risk Grade Badge Component
import React from 'react';
import { useTranslation } from 'react-i18next';
import './RiskGradeBadge.css';

interface RiskGradeBadgeProps {
  grade: string | null | undefined;
  score?: number | null;
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

const RiskGradeBadge: React.FC<RiskGradeBadgeProps> = ({
  grade,
  score,
  size = 'medium',
  showTooltip = true
}) => {
  const { t } = useTranslation();

  if (!grade) {
    return null;
  }

  const getGradeColor = (g: string): string => {
    switch (g.toUpperCase()) {
      case 'A': return '#10b981'; // Green - Excellent
      case 'B': return '#22c55e'; // Light green - Good
      case 'C': return '#eab308'; // Yellow - Acceptable
      case 'D': return '#f97316'; // Orange - Poor
      case 'E': return '#ef4444'; // Red - Bad
      case 'F': return '#dc2626'; // Dark red - Critical
      default: return '#6b7280'; // Gray - Unknown
    }
  };

  const getGradeLabel = (g: string): string => {
    switch (g.toUpperCase()) {
      case 'A': return t('riskGrade.excellent', 'Excellent');
      case 'B': return t('riskGrade.good', 'Good');
      case 'C': return t('riskGrade.acceptable', 'Acceptable');
      case 'D': return t('riskGrade.poor', 'Poor');
      case 'E': return t('riskGrade.bad', 'Bad');
      case 'F': return t('riskGrade.critical', 'Critical');
      default: return t('riskGrade.unknown', 'Unknown');
    }
  };

  const color = getGradeColor(grade);
  const label = getGradeLabel(grade);
  const tooltip = showTooltip
    ? `${t('riskGrade.securityRating', 'Security Rating')}: ${grade} - ${label}${score !== undefined && score !== null ? ` (${t('riskGrade.score', 'Score')}: ${score.toFixed(1)})` : ''}`
    : undefined;

  return (
    <span
      className={`risk-grade-badge risk-grade-${size}`}
      style={{
        backgroundColor: color,
        color: 'white'
      }}
      title={tooltip}
    >
      {grade.toUpperCase()}
    </span>
  );
};

export default RiskGradeBadge;
